import { Product } from '@/models/Product';
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { fileToBase64 } from '@/support/String';
import MiFileUpload from '@/components/mi-file-upload/MiFileUpload';
import AttributeSelector from '@/components/AttributeSelector/AttributeSelector';
import { Supplier } from '@/models/Supplier';
import { Brand } from '@/models/Brand';
import { Category } from '@/models/Category';
import { ImageAction } from '@/components/ImageCollection/ImageCollection';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';

@Component<BulkUpdateDialog>({
  components: {
    AttributeSelector
  }
})
export default class BulkUpdateDialog extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */

  @Prop()
  protected products!: Product[];
  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */

  @Ref() readonly fileUpload!: MiFileUpload;
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected isLoading = true;
  protected currentStep = 1;

  protected acceptedFileTypes = [
    'image/png',
    'image/jpeg',
    'image/svg+xml',
  ];
  protected uploadedFiles: UploadedFile[] = [];
  protected selectedFile: UploadedFile = {};
  protected previewFiles: previewFile[] = [];

  protected internalProduct: Product = new Product();

  protected categories: Category[] | null = null;
  protected categorySearch: string = "";
  protected searchCategoryDebounce: Function = debounce((searchInput: string) => this.getCategories(searchInput), 400);

  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected mounted() {
    this.initialize();
  }
  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected async initialize() {
    this.isLoading = true;


    this.isLoading = false;
  }

  protected close(): void {
    this.$emit('input', false);
  }

  protected handleItemDropped(file: File) {
    if(!file) {
      return;
    }

    fileToBase64(file).then((encodedFile: string) => {
      const uploadedFile: UploadedFile = {
        url: encodedFile,
        name: file.name,
        file: file,
      };

      const previewFile: previewFile = {
        url_large: encodedFile,
      };
      this.previewFiles.push(previewFile);
      this.uploadedFiles.push(uploadedFile);
    });

    const fileUploadComponent = this.fileUpload;
    fileUploadComponent.uploaded();
  }
  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  protected async create(): Promise<void> {
    this.isLoading = true;

    try {
      const productPromises = this.products.map((product) => this.updateProduct(product));
      await Promise.all(productPromises);
      this.$emit('created');
      this.close();
    } catch (error) {
      ErrorHandler.network(error);
    }

    this.isLoading = false;
  }

  protected async updateProduct(product: Product): Promise<any> {
    const filePromises = this.uploadedFiles.map((image, index) => this.attachImage(image, product));
    await Promise.all(filePromises);
    return new Product(product).update(this.productPayload)
  }

  protected async attachImage(image: UploadedFile, product: Product): Promise<any> {
    return await new Product().attachImage(product.id).create({image: image.url});
  }

  protected async getCategories(filter: string = ""): Promise<void> {
    await new Category()
      .filter("search", filter)
      .limit(999) // so we can also search inside all the sub-items
      .all()
      .then((categories: Category[]) => {
        let parsedCategories: Category[] = [];
        categories.forEach((category: any) => {
          parsedCategories.push(category);
          if (category.sub_categories && category.sub_categories.length > 1) {
            category.sub_categories.forEach((subColor: Category) => {
              parsedCategories.push(subColor);
            });
          }
        });

        this.categories = parsedCategories;
      })
      .catch((error: AxiosError) => {});
  }

  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */

  protected get productPayload() {
    return {
      supplier: this.internalProduct.supplier && this.internalProduct.supplier.id ? this.internalProduct.supplier.id : '',
      brand: this.internalProduct.brand && this.internalProduct.brand.id ? this.internalProduct.brand.id : '',
      categories: this.internalProduct.categories ? this.internalProduct.categories.map(category => {
        return category.id;
      }) : [],
    }
  }

  protected get supplierModel(): Supplier {
    return new Supplier();
  }

  protected get brandModel(): Brand {
    return new Brand();
  }

  protected get categoryModel(): Category {
    return new Category();
  }

  protected get imageActions(): ImageAction[] {
    return [
      {
        click: (image, index) => {
          if (!image || index === undefined || index < 0) {
            return;
          }

          this.uploadedFiles.splice(index, 1);
          this.previewFiles.splice(index, 1);
        },
        icon: 'delete',
        class: 'delete',
      },
    ]
  }

  protected get steps(): Step[] {
    const steps = [
      {
        title: 'Product',
      },
      {
        title: 'foto\'s',
      },
      {
        title: 'Overzicht',
      },
    ];

    return steps;
  }
  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */

  @Watch("categorySearch")
  protected categorySearchChanged(category: string) {
    this.searchCategoryDebounce(category);
  }

  //#endregion
}

interface UploadedFile {
  url?: string;
  name?: string;
  file?: any;
}

interface previewFile {
  url_large?: string;
  name?: string;
}

interface Step {
  title: string;
}
